import { Controller } from "../vendor/stimulus.js";

// FYI: The DEFAULT_SPEED is dramatically interconected with the other
// animations in this project, if you want to change that make sure it fits
// with other animation speeds.
const DEFAULT_SPEED = 16;

export default class extends Controller {
  static targets = [
    "item",
    "group"
  ];

  static values = {
    // For best results set this value between 10 and 20
    finishBefore: { type: Number, default: 12 },
  };

  connect() {
    this.activeTimeouts = new Set();
    this.activeIntervals = new Set();
    this.typewriterElements = this.itemTargets;
    if (this.typewriterElements.length === 0) return;

    this.prepareElements();
    this.startGroups();
    this.startStandalone();
  }

  disconnect() {
    this.activeTimeouts.forEach((id) => window.clearTimeout(id));
    this.activeIntervals.forEach((id) => window.clearInterval(id));
    this.activeTimeouts.clear();
    this.activeIntervals.clear();
  }

  prepareElements() {
    this.typewriterElements.forEach((el) => {
      const text = this.cleanText(el);
      el.dataset.typewriterText = text;
      this.buildCharacterSpans(el, text);

      const height = el.offsetHeight || el.scrollHeight || 0;
      if (height > 0) {
        el.dataset.typewriterHeight = height;
        el.style.minHeight = `${height}px`;
      }

      el.dataset.typewriterDisplay = window.getComputedStyle(el).display || "block";
      el.style.visibility = "hidden";
      el.classList.remove("typewriter--done");
    });
  }

  startGroups() {
    const groups =
      this.groupTargets.length > 0
        ? this.groupTargets
        : Array.from(this.element.querySelectorAll(".typewriter-group"));
    groups.forEach((group) => {
      this.runGroup(group);
    });
  }

  startStandalone() {
    this.typewriterElements.forEach((el) => {
      if (this.isInsideGroup(el)) return;
      const speed = this.readNumber(el.dataset.speed, DEFAULT_SPEED);
      const delay = this.readNumber(el.dataset.delay, 0);
      this.runTypewriter(el, { speed, delay });
    });
  }

  runGroup(group) {
    const items = Array.from(group.querySelectorAll(this.targetSelector("item")));
    const groupSpeed = this.readNumber(group.dataset.speed, DEFAULT_SPEED);

    const finishBefore = this.finishBeforeValue || 0;
    let completed = false;
    const dispatchComplete = () => {
      if (completed) return;
      completed = true;
      this.dispatch("complete", {
        detail: { group },
        target: group,
        bubbles: true,
      });
    };

    const sequence = items.reduce((chain, el, index) => {
      return chain.then(() => {
        const speed = this.readNumber(el.dataset.speed, groupSpeed || DEFAULT_SPEED);
        const delay = this.readNumber(el.dataset.delay, 0);
        const isLastItem = index === items.length - 1;
        const extraOptions = isLastItem && finishBefore > 0
          ? { finishBefore, onFinishBefore: dispatchComplete }
          : {};
        return this.runTypewriter(el, { speed, delay, ...extraOptions });
      });
    }, Promise.resolve());

    sequence.then(dispatchComplete);

    return sequence;
  }

  runTypewriter(el, options = {}) {
    const {
      speed = DEFAULT_SPEED,
      delay = 0,
      finishBefore = 0,
      onFinishBefore = null,
    } = options;
    const spans = Array.from(el.querySelectorAll(".typewriter-char"));
    const totalCharacters = spans.length;
    const safeSpeed = speed > 0 ? speed : DEFAULT_SPEED;
    const safeDelay = delay > 0 ? delay : 0;
    const triggerIndex = finishBefore > 0
      ? Math.max(0, totalCharacters - finishBefore)
      : null;
    let finishBeforeTriggered = false;

    el.setAttribute("aria-live", "polite");
    el.style.visibility = "hidden";

    return new Promise((resolve) => {
      if (totalCharacters === 0) {
        el.style.visibility = "visible";
        el.classList.add("typewriter--done");
        this.releaseMinHeight(el);
        resolve();
        return;
      }

      const timeoutId = window.setTimeout(() => {
        this.activeTimeouts.delete(timeoutId);
        el.style.visibility = "visible";

        let index = 0;
        const intervalId = window.setInterval(() => {
          if (index >= totalCharacters) {
            window.clearInterval(intervalId);
            this.activeIntervals.delete(intervalId);
            el.classList.add("typewriter--done");
            this.releaseMinHeight(el);
            resolve();
            return;
          }
          const currentSpan = spans[index];
          if (currentSpan) {
            const spanToReveal = currentSpan;
            window.requestAnimationFrame(() => {
              spanToReveal.getBoundingClientRect();
              setTimeout(() => {
                spanToReveal.classList.add("typewriter-char--visible");
              }, 0);
            });
          }
          if (
            triggerIndex !== null &&
            !finishBeforeTriggered &&
            index >= triggerIndex
          ) {
            finishBeforeTriggered = true;
            if (typeof onFinishBefore === "function") {
              onFinishBefore(el);
            }
          }
          index += 1;
        }, safeSpeed);

        this.activeIntervals.add(intervalId);
      }, safeDelay);

      this.activeTimeouts.add(timeoutId);
    });
  }

  cleanText(el) {
    return el.textContent.replace(/\s+/g, " ").trim();
  }

  readNumber(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  releaseMinHeight(el) {
    if (!el) return;
    if (el.dataset.typewriterHeight) {
      el.style.minHeight = "";
      delete el.dataset.typewriterHeight;
    }
  }

  buildCharacterSpans(el, text = "") {
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }

    if (!text) return;

    const fragment = document.createDocumentFragment();
    const tokens = text.split(/(\s+)/);
    tokens.forEach((token) => {
      if (!token) return;
      if (/\s+/.test(token)) {
        fragment.appendChild(document.createTextNode(token));
        return;
      }
      const wordWrapper = document.createElement("span");
      wordWrapper.className = "typewriter-word";
      Array.from(token).forEach((char) => {
        const span = document.createElement("span");
        span.className = "typewriter-char";
        span.textContent = char;
        wordWrapper.appendChild(span);
      });
      fragment.appendChild(wordWrapper);
    });

    el.appendChild(fragment);
  }

  targetSelector(name) {
    return `[data-${this.identifier}-target="${name}"]`;
  }

  isInsideGroup(element) {
    return Boolean(
      element.closest(this.targetSelector("group")) ||
        element.closest(".typewriter-group")
    );
  }
}
